<template>
  <div class="dynamic-treatment-page">
    <AbtestStepper
      :game-id="gameId"
      :version-id="versionId"
      :abtest-id="abtestId"
      :abtest="abtest"
      :in-wip-version="inWipVersion"
      :loading="isLoading"
    >
      <div :class="$style.dynamicTreatmentPage">
        <h2>Dynamic Treatment</h2>
        <section :class="$style.dynamicTreatmentContent">
          <CohortItem
            v-for="(cohort, index) in cohorts"
            :key="cohort.id"
            :dynamic-segments="abtest?.dynamicSegments ?? []"
            :index="index + 1"
            :game-id="gameId"
            :version-id="versionId"
            :can-edit-treatment="canEditTreatment"
            :abtest-id="abtestId"
            :ab-test-state="abtest?.state"
            :cohort="cohort"
            :offers="offerOptions"
            :refetch-offers="refetchOffers"
            :routing-cohort-id="cohort.id === routingCohortId ? routingCohortId : ''"
            :routing-segment-id="routingSegmentId"
            @delete-cohort="() => onDeleteCohort(cohort.id)"
          />
          <vd-button v-if="canEditTreatment" type="primary" skin="blue" icon-left="fa:add" @click="createCohort">
            Add cohort
          </vd-button>
        </section>
        <section :class="$style.abTestActions">
          <vd-button type="secondary" icon-left="fa:chevron-left" @click="onGoBackToListPage">Back to list</vd-button>
          <div :class="$style.stepControls">
            <vd-button type="secondary" skin="blue" @click="onGoToPreviousPage">Previous</vd-button>
            <vd-button type="primary" :disabled="cohorts.length === 0" @click="onNext">Next</vd-button>
          </div>
        </section>
      </div>
    </AbtestStepper>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";

import AbtestStepper from "@/components/AbtestStepper/AbtestStepper.vue";
import {
  CohortFragment,
  CohortInput,
  useCohortFormPageSaveMutation,
  useCohortsListPageDeleteMutation,
  useCohortsListPageQuery,
  useGetCohortOfferOptionsQuery,
  useMutationResponse,
} from "@/graphql";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useCampaigns from "@/modules/Offers/hooks/useCampaigns";
import useMessages from "@/hooks/messages";
import links from "@/router/links";
import CohortItem from "./components/CohortItem.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  abtestId: {
    type: String,
    required: true,
  },
  routingCohortId: {
    type: String,
    default: "",
  },
  routingSegmentId: {
    type: String,
    default: "",
  },
});

const router = useRouter();
const { confirm } = useMessages();

const cohorts = ref<CohortFragment[]>([]);
const isLoading = ref(true);

/**
 * Display created cohorts
 */
const { result, refetch: refetchCohorts } = useCohortsListPageQuery(
  {
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId: props.abtestId,
  },
  { fetchPolicy: "network-only" }
);

const inWipVersion = computed(() => result.value?.game?.version?.isWip ?? false);
const abtest = computed(() => result.value?.game?.version?.currentAbtest ?? undefined);
const canEditTreatment = computed(() => inWipVersion.value && abtest.value?.state === "DRAFT");

// Slim offers list for the cohort offers editors, fetched ONCE for the page: one CohortItem renders per
// cohort, and a per-cohort copy of the (full) offers query froze the page for seconds on games with
// thousands of offers.
const { result: offersResult, refetch: refetchOfferOptions } = useGetCohortOfferOptionsQuery(
  { appId: props.gameId, versionId: props.versionId },
  { fetchPolicy: "cache-and-network" }
);
const offerOptions = computed(() => offersResult.value?.getOfferItems ?? []);
const refetchOffers = async () => refetchOfferOptions();

const form = reactive<CohortInput>({
  name: "Cohort 1",
  description: "",
  allocationRate: 0,
  isDynamic: true,
});

/**
 * Create the cohort
 */
const { mutate: saveCohort } = useCohortFormPageSaveMutation({});

const createCohort = async () => {
  const response = await saveCohort({
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId: props.abtestId,
    cohortId: null,
    input: form,
  });

  const data = useMutationResponse(response, (resp) => resp?.saveCohort);

  if (data?.success) {
    await refetchCohorts();
  }
};

/**
 * Delete a cohort
 */
const { flags } = useFeatureFlags(props.gameId);
const { mutate: deleteCohort } = useCohortsListPageDeleteMutation({});

// Campaign names for the cohort offers editors (flag on): fetched ONCE here — a CohortItem renders per
// cohort and they all read useCampaigns' shared state, so fetching in the item would fire one /campaigns
// request per cohort. Watched because the flags load asynchronously.
const { getCampaigns } = useCampaigns(props.gameId, props.versionId);
watch(
  () => flags.value.offerMultiLinkABTest.enabled,
  (enabled) => {
    if (enabled) {
      getCampaigns();
    }
  },
  { immediate: true }
);

const onDeleteCohort = async (cohortId: string) => {
  const confirmed = await confirm("Do you really want to delete this cohort?");

  if (confirmed) {
    const response = await deleteCohort({
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
      cohortId,
      useMultiLink: flags.value.offerMultiLinkABTest.enabled,
    });

    const data = useMutationResponse(response, (resp) => resp?.deleteCohort);
    if (data?.success) {
      await refetchCohorts();
    }
  }
};

/**
 * Routing
 */
const onNext = async () => {
  router.push({
    name: links.games.abtests.cohorts.list,
    params: {
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
    },
  });
};

const onGoBackToListPage = async () => {
  router.push({ name: links.games.abtests.list });
};

const onGoToPreviousPage = async () => {
  router.push({
    name: links.games.abtests.edit,
    params: { gameId: props.gameId, versionId: props.versionId, abtestId: props.abtestId },
  });
};

watch(
  () => result.value,
  () => {
    cohorts.value = result.value?.game?.version?.currentAbtest?.cohorts ?? [];
    form.name = `Cohort ${cohorts.value.length + 1}`;
    isLoading.value = false;
  }
);
</script>

<style lang="scss" module>
.dynamicTreatmentPage {
  h2 {
    margin-bottom: 2rem;
  }

  .controlTitle {
    margin-bottom: 0.5rem;
  }

  .controlSegments {
    padding: 2px 7px;
    border-radius: 0.5rem;
    margin-right: 0.5rem;
    font-size: 12px;
    background-color: var(--vd-color-blue-light-9);
    color: var(--vd-color-text-default);
  }

  .dynamicTreatmentContent {
    margin-top: 2rem;
  }

  .abTestActions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: size("XL");

    .stepControls {
      display: flex;
      align-items: center;
    }
  }
}
</style>
